import { useEffect, useRef, useState } from "react";
import { mergeStyles } from "@fluentui/react";

interface TerminalInstance {
    open(container: HTMLElement): void;
    write(text: string): void;
    cols: number;
    buffer: { active: { cursorX: number } };
    resize(columns: number, rows: number): void;
    dispose(): void;
    onData(handler: (data: string) => void): { dispose(): void };
    attachCustomKeyEventHandler(handler: (event: KeyboardEvent) => boolean): void;
}

interface TerminalConstructor {
    new(options: {
        cursorBlink: boolean;
        fontFamily: string;
        fontSize: number;
        lineHeight: number;
        scrollback: number;
        screenReaderMode: boolean;
        theme: { background: string; foreground: string; cursor: string };
    }): TerminalInstance;
}

let terminalLibrary: Promise<TerminalConstructor> | undefined;

const terminalContainerClass = mergeStyles({
    selectors: {
        '.xterm-helper-textarea': { caretColor: 'transparent' },
    },
});

const loadTerminal = (): Promise<TerminalConstructor> => {
    if (!terminalLibrary) {
        terminalLibrary = new Promise<TerminalConstructor>((resolve, reject) => {
            const stylesheet = document.createElement('link');
            stylesheet.rel = 'stylesheet';
            stylesheet.href = 'https://cdn.jsdelivr.net/npm/@xterm/xterm@5.5.0/css/xterm.css';
            stylesheet.integrity = 'sha384-8Xk9wy/gzEDUKrXtrmCFa2bBuK3BpjpDuL/p0SeKQX19Khl/M+lHOgD/CyYf7efP';
            stylesheet.crossOrigin = 'anonymous';
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/@xterm/xterm@5.5.0/lib/xterm.js';
            script.integrity = 'sha384-M169f14mRZOXm3hD/v2Ti0ThIT/RnAQagXA9nlE15yHAtrW19gdePJh/HaTzUOe/';
            script.crossOrigin = 'anonymous';
            script.async = true;
            let assetsLoaded = 0;
            const cleanup = () => {
                window.clearTimeout(timeout);
                script.onload = null;
                script.onerror = null;
                stylesheet.onload = null;
                stylesheet.onerror = null;
            };
            const fail = () => {
                cleanup();
                script.remove();
                stylesheet.remove();
                reject(new Error('Unable to load xterm.js. Check your connection and Content Security Policy for cdn.jsdelivr.net.'));
            };
            const loaded = () => {
                if (++assetsLoaded !== 2)
                    return;
                const constructor = (window as Window & { Terminal?: TerminalConstructor }).Terminal;
                if (!constructor) {
                    fail();
                    return;
                }
                cleanup();
                resolve(constructor);
            };
            const timeout = window.setTimeout(fail, 15000);
            script.onload = loaded;
            stylesheet.onload = loaded;
            script.onerror = fail;
            stylesheet.onerror = fail;
            document.head.append(stylesheet, script);
        }).catch(error => {
            terminalLibrary = undefined;
            throw error;
        });
    }
    return terminalLibrary;
};

interface AdvancedTerminalProps {
    lines: string[];
    busy: boolean;
    commands: readonly string[];
    onCommand(command: string): void;
    onError(error: Error): void;
}

// Never interpret command output or pasted input as terminal control sequences.
const plainText = (text: string) => Array.from(text)
    .filter(character => character === '\n' || (character.charCodeAt(0) >= 32 && !(character.charCodeAt(0) >= 127 && character.charCodeAt(0) <= 159)))
    .join('');

export const AdvancedTerminal = (props: AdvancedTerminalProps) => {
    const container = useRef<HTMLDivElement>(null);
    const terminal = useRef<TerminalInstance>();
    const current = useRef(props);
    current.current = props;
    const input = useRef('');
    const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
    const [attempt, setAttempt] = useState(0);

    const render = () => {
        const instance = terminal.current;
        if (!instance)
            return;
        instance.write('\x1b[H\x1b[2J' +
            current.current.lines.map(plainText).join('\n').replace(/\n/g, '\r\n') +
            `\r\n\r\n${current.current.busy ? 'Running... ' : '> '}${input.current}`);
    };
    const renderRef = useRef(render);
    renderRef.current = render;

    useEffect(() => {
        let cancelled = false;
        let subscription: { dispose(): void } | undefined;
        let observer: ResizeObserver | undefined;
        setLoadState('loading');
        loadTerminal().then(Terminal => {
            if (cancelled || !container.current)
                return;
            const instance = new Terminal({
                cursorBlink: true,
                fontFamily: 'Consolas, monospace',
                fontSize: 13,
                lineHeight: 1.3,
                scrollback: 1000,
                screenReaderMode: true,
                theme: { background: '#070a10', foreground: '#c5d0e0', cursor: '#9bb0cb' },
            });
            terminal.current = instance;
            instance.open(container.current);
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (!context)
                throw new Error('Unable to measure terminal text.');
            context.font = '13px Consolas, monospace';
            const cellWidth = context.measureText('W').width;
            observer = new ResizeObserver(() => {
                if (container.current && container.current.clientWidth > 0) {
                    const columns = Math.max(2, Math.floor((container.current.clientWidth - 20) / cellWidth));
                    if (columns !== instance.cols)
                        instance.resize(columns, 14);
                }
            });
            observer.observe(container.current);
            const completeCommand = () => {
                if (current.current.busy)
                    return;
                const prefix = input.current.toLowerCase();
                const matches = current.current.commands.filter(command => command.startsWith(prefix));
                if (!matches.length) {
                    instance.write('\x07');
                    return;
                }
                let completion = matches[0];
                for (const match of matches) {
                    while (!match.startsWith(completion))
                        completion = completion.slice(0, -1);
                }
                const suffix = completion.slice(prefix.length);
                input.current += suffix;
                instance.write(suffix);
                if (matches.length > 1)
                    instance.write(`\r\n${matches.join('  ')}\r\n> ${input.current}`);
            };
            instance.attachCustomKeyEventHandler(event => {
                if (event.key === 'Tab' && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) {
                    if (event.type === 'keydown') {
                        event.preventDefault();
                        completeCommand();
                    }
                    return false;
                }
                return true;
            });
            subscription = instance.onData(data => {
                if (current.current.busy)
                    return;
                if (data === '\t') {
                    completeCommand();
                } else if (data === '\r') {
                    const command = input.current;
                    input.current = '';
                    current.current.onCommand(command);
                    if (!command.trim())
                        instance.write('\r\n> ');
                } else if (data === '\x7f') {
                    if (input.current.length) {
                        input.current = input.current.slice(0, -1);
                        instance.write(instance.buffer.active.cursorX === 0
                            ? `\x1b[A\x1b[${instance.cols}G\x1b[X`
                            : '\b \b');
                    }
                } else if (data === '\x03' || data === '\x15') {
                    input.current = '';
                    instance.write('\r\n> ');
                } else if (!data.includes('\x1b')) {
                    const text = data.replace(/[^\x20-\x7e]/g, '').slice(0, 200 - input.current.length);
                    input.current += text;
                    instance.write(text);
                }
            });
            setLoadState('ready');
            renderRef.current();
        }).catch(error => {
            if (cancelled)
                return;
            observer?.disconnect();
            subscription?.dispose();
            terminal.current?.dispose();
            terminal.current = undefined;
            setLoadState('error');
            current.current.onError(error instanceof Error ? error : new Error(String(error)));
        });
        return () => {
            cancelled = true;
            observer?.disconnect();
            subscription?.dispose();
            terminal.current?.dispose();
            terminal.current = undefined;
        };
    }, [attempt]);

    useEffect(() => {
        renderRef.current();
    }, [props.lines, props.busy]);

    return <div>
        {loadState === 'loading' && <p role="status">Loading terminal...</p>}
        {loadState === 'error' &&
            <div role="alert">
                <p>Unable to load the terminal. Check CDN access and the content security policy, then try again.</p>
                <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
            </div>
        }
        <div ref={container} className={terminalContainerClass} aria-label="Advanced command terminal" style={{ minWidth: 0, height: '260px', padding: '10px', background: '#070a10', borderRadius: '8px', overflow: 'hidden', display: loadState === 'error' ? 'none' : 'block' }} />
    </div>;
};
